import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  Checkbox,
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
  Input,
  Modal,
  Tag,
} from '@unified-checkout/design-system';
import {
  AnimatedLoadingIndicator,
  Plus,
} from '@unified-checkout/design-system/icons/index';

import type { IDropdownOption } from '@app/dashboard/types/innerLayout/risk_center/modals';
import type { IField } from '@app/dashboard/types/outerLayout/common';
import type {
  TRiskCenterTypes,
  TAddEditPayload,
  TAddFn,
  TEditFn,
} from '@app/dashboard/types/innerLayout/risk_center';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { getModalRuleData } from '@app/dashboard/services/inner_layout/risk_center/rules';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { useLangStore } from '@app/dashboard/store/lang';
import useMaxPriorityStore from '@app/dashboard/store/riskCenter';
import {
  ACTIONS,
  ADD_EDIT_MODAL_API_MAP,
  ADD_EDIT_MODAL_CONFIG,
  INITIAL_ADD_EDIT_FIELDS_STATE,
  LIST_FIELD_TYPE,
  LIST_TYPE_OPTIONS,
  RISK_CENTER_TYPES,
  VALIDATION_REQUIRED_MESSAGE,
} from '@app/dashboard/constants/risk_center';
import { SYSTEM_EVENTS_SRC_FEATURES } from '@app/dashboard/constants/tracking_events/system';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { COUNTRIES } from '@app/dashboard/constants/risk_center/countries';
import { onFieldChangeHandler } from '@app/dashboard/utils/formValidations';
import { onToasterShowHandler } from '@app/dashboard/utils/tracking/onToasterShowHandler';
import { getMerchantId } from '@app/dashboard/utils/localStorageUtils';
import { truncateString } from '@app/dashboard/utils/truncateString';
import { notify, useNotificationStore } from '@app/dashboard/utils/notifier';
import { NOTIFICATION_STATUS } from '@app/dashboard/constants/notifier';
import {
  getListTypeValue,
  handleTypeNameMapper,
  normalizedOptions,
} from '../../utils/modals';

import styles from './AddEditModal.module.scss';

const AddEditModal = ({
  isModalOpen,
  setIsShareModalOpen,
  currentTab,
  action = ACTIONS.ADD,
  id,
  existingData,
}: {
  isModalOpen: boolean;
  setIsShareModalOpen:
    | Dispatch<SetStateAction<boolean>>
    | ((open: boolean) => void);
  currentTab: string;
  action?: 'add' | 'edit';
  id?: string | undefined;
  existingData?: Record<string, any> | undefined;
}) => {
  const { t } = useTranslation();
  const { isLive } = useTestLiveModeStore();
  const { lang } = useLangStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const maxPriority = useMaxPriorityStore((state) => state.maxPriority);
  const screenWidth = useScreenWidth();
  const { show: isNotifyShown, state: notifyState } = useNotificationStore();

  const [selectedOption, setSelectedOption] = useState<IDropdownOption | null>(
    null
  );
  const [tags, setTags] = useState<string[]>([]);
  const [fields, setFields] = useState<IField>(
    INITIAL_ADD_EDIT_FIELDS_STATE(t)
  );
  const [active, setActive] = useState(false);
  const [country, setCountry] = useState({
    Code: '',
    Name: '',
  });
  const [riskCenterName, setRiskCenterName] = useState('');
  const [isTagsTouched, setIsTagsTouched] = useState(false);

  const {
    isFetching: isModalRulesFetching,
    data: { data: modalRuleData } = {},
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_RULES_MODAL_DATA],
    queryFn: () => getModalRuleData(isLive),
    enabled: currentTab === RISK_CENTER_TYPES.RULES,
    staleTime: 5 * 60 * 1000,
  });

  const modalConfig =
    ADD_EDIT_MODAL_CONFIG[currentTab as keyof typeof ADD_EDIT_MODAL_CONFIG];

  const currentField = selectedOption
    ? fields[selectedOption?.value as string]
    : undefined;

  const isMobile = screenWidth <= MOBILE_BREAKPOINT;
  const hasTags = !!tags.length;
  const errorMsg = currentField?.errorMsg;
  const isRequiredError = errorMsg === VALIDATION_REQUIRED_MESSAGE(t);
  const error = hasTags && isRequiredError ? '' : errorMsg;
  const status =
    errorMsg && (!hasTags || !isRequiredError) ? 'error' : 'default';

  const isDisableSave = useMemo(() => {
    if (!selectedOption) return true;

    const hasName = riskCenterName.trim().length > 0;
    const hasTags = tags.length > 0;
    const hasError = !!currentField?.errorMsg;

    // Special handling for RULES tab
    if (currentTab === RISK_CENTER_TYPES.RULES) {
      return !selectedOption?.value || isNaN(maxPriority);
    }

    const isCountryType = selectedOption.value === LIST_FIELD_TYPE.COUNTRY;

    return (
      !hasName ||
      ((isCountryType ? !country.Code : !hasTags) && action === ACTIONS.ADD) ||
      hasError
    );
  }, [
    selectedOption,
    tags,
    riskCenterName,
    currentField?.errorMsg,
    currentTab,
    country.Code,
  ]);

  const options = useMemo(
    () =>
      isModalRulesFetching
        ? []
        : currentTab === RISK_CENTER_TYPES.RULES
        ? modalRuleData
        : LIST_TYPE_OPTIONS(t),
    [t, currentTab, isModalRulesFetching, modalRuleData]
  );

  const handleSelectOption = useCallback(
    (option: IDropdownOption | null) => {
      setSelectedOption(option);
      if (currentTab === RISK_CENTER_TYPES.RULES) return;
      setTags([]);
      setIsTagsTouched(false);
      if (option) {
        setFields((prev) => ({
          ...prev,
          [option.value as string]: {
            ...prev[option.value as string],
            value: '',
            isValid: false,
            errorMsg: '',
          },
        }));
      }
    },
    [currentTab]
  );

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRiskCenterName(e.target.value);
  };

  const handleTagAdd = (tag: string) => {
    if (!currentField?.value || !selectedOption) return;
    const trimmedTag = tag.trim();

    if (tags.includes(trimmedTag)) {
      setFields((prevFields) => ({
        ...prevFields,
        [selectedOption?.value as string]: {
          ...prevFields[selectedOption?.value as string],
          errorMsg: t('innerLayout.riskCenter.errors.duplicateEntry'),
        },
      }));

      return false;
    }

    setTags((prevTags) => [...prevTags, trimmedTag]);
    setFields((prevFields) => ({
      ...prevFields,
      [selectedOption?.value as string]: {
        ...prevFields[selectedOption?.value as string],
        value: '',
        isValid: false,
        errorMsg: '',
      },
    }));
    setIsTagsTouched(true);
    return true;
  };

  const handleTagRemove = (tag: string) => {
    const trimmedTag = tag.trim();
    setTags((prevTags) => prevTags.filter((t) => t !== trimmedTag));
  };

  const handleActiveChange = (checked: boolean) => {
    setActive(checked);
  };

  const handleFieldChange = (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    const targetValue =
      typeof e === 'string' ? e : (e.target as HTMLInputElement).value;
    onFieldChangeHandler(
      fields,
      setFields,
      selectedOption?.value as string,
      targetValue,
      () => {}
    );
  };

  const handleResetFields = () => {
    setSelectedOption(null);
    setTags([]);
    setFields(INITIAL_ADD_EDIT_FIELDS_STATE(t));
    setActive(false);
    setRiskCenterName('');
    setIsTagsTouched(false);
  };

  const { mutate: handleAddEditSubmit, isPending } = useMutation({
    mutationFn: () => {
      let payload;
      if (currentTab !== RISK_CENTER_TYPES.RULES) {
        payload = {
          reason: riskCenterName,
          active,
          merchant: getMerchantId(),
        };

        if (currentTab === RISK_CENTER_TYPES.BLACKLIST) {
          payload = {
            ...payload,
            blacklist_type: selectedOption?.value,
            blacklist_data:
              country.Code ||
              (action === ACTIONS.ADD ? tags.join(',') : currentField?.value),
          };
        } else if (currentTab === RISK_CENTER_TYPES.WHITELIST) {
          payload = {
            ...payload,
            whitelist_type: selectedOption?.value,
            whitelist_data:
              country.Code ||
              (action === ACTIONS.ADD ? tags.join(',') : currentField?.value),
          };
        }
      } else {
        payload = {
          merchant: getMerchantId(),
          active,
          rule: Number(selectedOption?.value),
          priority: maxPriority ? maxPriority + 1 : 1,
        };
      }
      const queryFn =
        ADD_EDIT_MODAL_API_MAP[action][currentTab as TRiskCenterTypes];

      if (action === ACTIONS.EDIT) {
        return (queryFn as TEditFn)(Number(id), payload as TAddEditPayload);
      } else {
        return (queryFn as TAddFn)(payload as TAddEditPayload);
      }
    },
    onSuccess: () => {
      const successMessage = t(
        action === ACTIONS.EDIT
          ? modalConfig.UPDATED_SUCCESSFULLY_KEY
          : modalConfig.CREATED_SUCCESSFULLY_KEY
      );
      notify.success(successMessage, {
        onShowHandler: () =>
          onToasterShowHandler({
            type: 'success',
            trackingCode: modalConfig.TRACKING_CODE_OBJ?.[action.toUpperCase()],
            msg: successMessage,
            srcFeature:
              action === 'add'
                ? SYSTEM_EVENTS_SRC_FEATURES.RISK_CENTER.MAIN
                : SYSTEM_EVENTS_SRC_FEATURES.RISK_CENTER.DETAILS,
          }),
      });
      setTimeout(() => {
        setIsShareModalOpen(false);
        handleResetFields();
        if (action === ACTIONS.EDIT) {
          navigate('/risk-center');
        } else {
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_RISK_CENTER_DATA, isLive, currentTab],
          });
        }
      }, 2000);
    },
    onError: (axiosErr: AxiosError<{ message?: string }>) => {
      let message = t('errors.somethingWentWrong');

      if (axiosErr.response?.data?.message) {
        message = axiosErr.response?.data?.message;
      } else if (axiosErr.message) {
        message = axiosErr.message;
      }

      notify.error(message, {
        onShowHandler: () =>
          onToasterShowHandler({
            type: 'error',
            trackingCode: modalConfig.TRACKING_CODE_OBJ?.[action.toUpperCase()],
            msg: message,
            srcFeature:
              action === 'add'
                ? SYSTEM_EVENTS_SRC_FEATURES.RISK_CENTER.MAIN
                : SYSTEM_EVENTS_SRC_FEATURES.RISK_CENTER.DETAILS,
          }),
      });
    },
  });

  useEffect(() => {
    return () => {
      handleResetFields();
    };
  }, [isModalOpen]);

  useEffect(() => {
    if (
      selectedOption?.value === LIST_FIELD_TYPE.COUNTRY &&
      !currentField?.isValid
    ) {
      setCountry({ Code: '', Name: '' });
    }
  }, [selectedOption, currentField]);

  useEffect(() => {
    if (!tags.length && selectedOption && isTagsTouched) {
      setFields((prevFields) => ({
        ...prevFields,
        [selectedOption?.value as string]: {
          ...prevFields[selectedOption?.value as string],
          isValid: false,
          errorMsg: t('errors.required'),
        },
      }));
    }
  }, [tags, selectedOption, isTagsTouched]);

  useEffect(() => {
    if (
      action === ACTIONS.EDIT &&
      existingData &&
      currentTab !== RISK_CENTER_TYPES.RULES
    ) {
      setRiskCenterName(existingData.reason || '');
      setActive(!!existingData.active);

      const typeMapper = handleTypeNameMapper(currentTab);
      const option = getListTypeValue(
        typeMapper ? existingData[typeMapper.type] : ''
      );

      const typeOption = normalizedOptions(options, lang).find(
        (opt) => opt.value === option
      );

      if (typeOption?.value === LIST_FIELD_TYPE.COUNTRY && typeMapper) {
        setCountry({
          Code: existingData[typeMapper.data],
          Name:
            COUNTRIES.find(
              (country) => country.Code === existingData[typeMapper.data]
            )?.Name || '',
        });
      }

      setSelectedOption(typeOption || null);
      setFields((prev) => ({
        ...prev,
        [option]: {
          ...prev[option],
          value: typeMapper ? existingData[typeMapper.data] : '',
          isValid: true,
          errorMsg: '',
        },
      }));
    }
  }, [action, existingData, currentTab, isModalOpen, lang]);

  return (
    <Modal
      open={isModalOpen}
      onCloseHandler={() => setIsShareModalOpen(false)}
      title={t(
        action === ACTIONS.EDIT
          ? modalConfig.EDIT_MODAL_TITLE_KEY
          : modalConfig.TITLE_KEY
      )}
      wrapperClassName={styles['add-edit-modal']}
      btnsProps={[
        {
          text: t('form.save'),
          variant: 'contained',
          onClickHandler: () => handleAddEditSubmit(),
          disabled: isDisableSave || isPending || (isNotifyShown && notifyState === NOTIFICATION_STATUS.SUCCESS),
          loading: isPending,
        },
        {
          text: t('form.cancel'),
          variant: 'outlined',
          onClickHandler: () => setIsShareModalOpen(false),
          color: 'secondary',
          disabled: isPending || (isNotifyShown && notifyState === NOTIFICATION_STATUS.SUCCESS),
        },
      ]}
      closeOnClickOutside
      isOverlayAboveModal
      hasCloseIcon
    >
      <div className={styles['add-edit-modal__content']}>
        <Dropdown width="100%" label={t(modalConfig.TYPE_LABEL_KEY)}>
          <DropdownButton
            placeholder={t(modalConfig.TYPE_LABEL_PLACEHOLDER_KEY)}
            text={
              selectedOption?.label
                ? truncateString(selectedOption.label, isMobile ? 30 : 60)
                : ''
            }
            disabled={isModalRulesFetching || isPending}
            {...(isModalRulesFetching && {
              leadingIcon: AnimatedLoadingIndicator,
            })}
          />
          <DropdownMenu maxHeight={isMobile ? '10rem' : '20rem'}>
            {normalizedOptions(options, lang).map((option, i) => (
              <DropdownMenuItem
                key={`option-${option.value}-${i}`}
                text={option.label}
                selected={selectedOption?.value === option.value}
                onClickHandler={() => handleSelectOption(option)}
              />
            ))}
          </DropdownMenu>
        </Dropdown>
        {currentTab !== RISK_CENTER_TYPES.RULES && (
          <>
            <Input
              name="list-name"
              label={t(modalConfig.NAME_LABEL_KEY) || 'Name'}
              placeholder={t(modalConfig.NAME_LABEL_PLACEHOLDER_KEY)}
              onChange={handleNameChange}
              disabled={isPending}
              value={riskCenterName}
            />
            <div className={styles['add-edit-modal__tags-input']}>
              <div
                className={styles['add-edit-modal__tags-input-field']}
                data-edit-add-modal-input-field-error={!!currentField?.errorMsg}
              >
                {selectedOption?.value !== LIST_FIELD_TYPE.COUNTRY ? (
                  <>
                    <Input
                      type="text"
                      id="list-data"
                      name={selectedOption?.value as string}
                      placeholder={t(modalConfig.DATA_LABEL_PLACEHOLDER_KEY)}
                      label={t(modalConfig.DATA_LABEL_KEY) || 'Data'}
                      onChange={handleFieldChange}
                      disabled={!selectedOption?.value || isPending}
                      value={currentField?.value || ''}
                      error={error}
                      status={status}
                    />
                    {action === ACTIONS.ADD && (
                      <Button
                        disabled={!currentField?.isValid || isPending}
                        leadingIcon={Plus}
                        onClickHandler={() => {
                          handleTagAdd(currentField?.value || '');
                        }}
                      />
                    )}
                  </>
                ) : (
                  <Dropdown>
                    <DropdownButton
                      placeholder={t(modalConfig.DATA_LABEL_PLACEHOLDER_KEY)}
                      text={country?.Name || ''}
                      disabled={!selectedOption?.value || isPending}
                    />
                    <DropdownMenu>
                      {COUNTRIES.map((country) => (
                        <DropdownMenuItem
                          key={country.Code}
                          text={country.Name}
                          onClickHandler={() => {
                            handleFieldChange(country.Code);
                            setCountry(country);
                          }}
                        />
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                )}
              </div>
              {action === ACTIONS.ADD && (
                <div
                  className={styles['add-edit-modal__tags-input-list']}
                  data-tags-loading={isPending}
                >
                  {tags.map((tag, index) => (
                    <Tag
                      key={`${tag}-${index}`}
                      id={`${tag}-${index}`}
                      type="text"
                      size="sm"
                      text={tag}
                      onClose={() => handleTagRemove?.(tag)}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
        <Checkbox
          id="active-rule-list"
          size="md"
          text={t(modalConfig.ENABLED_LABEL_KEY) || 'Enabled'}
          hintText={t(modalConfig.NOTE_KEY) || ''}
          checked={active}
          onChangeHandler={handleActiveChange}
          disabled={isPending}
        />
      </div>
    </Modal>
  );
};

export default AddEditModal;
